import React, { useCallback, useEffect, useState } from "react";
import { Plus, Save, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { AdminHeader, Panel, borderStyle, inputCls } from "../ui";
import { PageLoader } from "../../components/States";

export default function ReviewsAdmin() {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => {
    adminApi.get("/admin/reviews").then((r) => setRows(r.data.reviews)).catch(() => setRows([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save() {
    try {
      const body = {
        body: edit.body, author: edit.author, role: edit.role,
        rating: Number(edit.rating || 5), source_link: edit.source_link,
        published: edit.published, order: Number(edit.order || 0),
      };
      if (edit.id) await adminApi.put(`/admin/reviews/${edit.id}`, body);
      else await adminApi.post("/admin/reviews", body);
      toast.success("Saved");
      setEdit(null);
      load();
    } catch (err) {
      toast.error("Save failed", { description: err?.response?.data?.detail });
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this review?")) return;
    await adminApi.delete(`/admin/reviews/${id}`);
    toast.success("Deleted");
    load();
  }

  return (
    <div data-testid="admin-reviews">
      <AdminHeader title="Reviews" subtitle={rows ? `${rows.length} customer reviews` : ""}>
        <button type="button" className="btn-gold" data-testid="admin-new-review-button"
          onClick={() => setEdit({ body: "", author: "", role: "", rating: 5, source_link: "", order: 0, published: true })}>
          <Plus className="h-4 w-4" /> Add review
        </button>
      </AdminHeader>

      {edit && (
        <Panel className="mb-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <input value={edit.author || ""} onChange={(e) => setEdit({ ...edit, author: e.target.value })} placeholder="Author name" className={inputCls} style={borderStyle} data-testid="review-author-input" />
            <input value={edit.role || ""} onChange={(e) => setEdit({ ...edit, role: e.target.value })} placeholder="Role e.g. Off-Road Expert" className={inputCls} style={borderStyle} />
            <input type="number" min="1" max="5" value={edit.rating} onChange={(e) => setEdit({ ...edit, rating: e.target.value })} placeholder="Rating" className={inputCls} style={borderStyle} />
            <textarea rows={4} value={edit.body || ""} onChange={(e) => setEdit({ ...edit, body: e.target.value })} placeholder="Review text" className={`${inputCls} sm:col-span-3`} style={borderStyle} data-testid="review-body-input" />
            <div className="flex gap-3 sm:col-span-3">
              <button type="button" onClick={save} className="btn-gold" data-testid="review-save-button"><Save className="h-4 w-4" /> Save</button>
              <button type="button" onClick={() => setEdit(null)} className="btn-outline-nq">Cancel</button>
            </div>
          </div>
        </Panel>
      )}

      {!rows ? <PageLoader /> : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {rows.map((r) => (
            <div key={r.id} className="card-nq p-5" data-testid="admin-review-item">
              <div className="flex">
                {Array.from({ length: r.rating || 5 }).map((_, k) => (
                  <Star key={k} className="h-3.5 w-3.5 fill-current gold-text" />
                ))}
              </div>
              <p className="mt-2 line-clamp-5 text-sm text-[color:var(--nq-stone)]">{r.body}</p>
              <p className="mt-3 text-sm font-semibold">{r.author}</p>
              {r.role && <p className="text-xs italic text-[color:var(--nq-stone)]">{r.role}</p>}
              <div className="mt-3 flex gap-3">
                <button type="button" onClick={() => setEdit(r)} className="text-xs font-semibold gold-text hover:underline" data-testid="admin-review-edit">Edit</button>
                <button type="button" onClick={() => remove(r.id)} className="flex items-center gap-1 text-xs text-[color:var(--nq-stone)] hover:text-[#e07a72]">
                  <Trash2 className="h-3 w-3" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
